import * as React from "react"
import { Form, Col, Button, Spinner } from "react-bootstrap"
import { Subscription } from "rxjs"
import { SampleType, GovPanel } from "../../api/Randoms/GovPoolModel/CollectionTypeResponse"
import GovGetCollectionTypes from "../../api/Randoms/GovGetCollectionTypes"
import LabAccountsGetObservable from "../../api/LabService/LabAccountsGet"
import LabAccount from "../../api/LabService/LabServiceModel/LabAccount"
import GovDeviceGetObservable from "../../api/Randoms/GovDevicesGet"
import GovDevice from "../../api/Randoms/GovPoolModel/GovDevice"
import { Typeahead } from "react-bootstrap-typeahead"
import { LabAccountMaxLength } from "../../config/Constants"
import { toast } from "react-toastify"
import GovGetPanels from "../../api/Randoms/GovGetPanels"
import FormatError from "../../formatters/FormatError"
import { DeviceSampleType } from "./DeviceSampleType"
import { FaAngleDown } from "react-icons/fa"

interface GovCollectionProps {
  editing?: boolean
  pendingRandom?: boolean
  showHeading?: boolean
  labAccount?: string
  serviceDOT: boolean
  sampleTypeID?: number
  panel?: string
  device?: string
  companyID: number
  labelClassName?: string
  onTypeChange: (sampleType?: SampleType, isPOCTDevice?: boolean) => void
  onLabChange: (labAccount?: LabAccount) => void
  onPanelChange: (panel?: GovPanel) => void
  onDeviceChange: (device?: GovDevice) => void
}

interface LabAccountType {
  isUrineLab: boolean
  isSalivaLab: boolean
  isUrinePOCT: boolean
  isSalivaPOCT: boolean
}

export const getLabType = (lab: LabAccount): LabAccountType => {
  const regionID = (lab.LabAccount || "").split(".")[1] || ""
  const isUrineLab = !(regionID.startsWith("ORF") || regionID.startsWith("CON") || regionID.startsWith("OCO") || regionID.startsWith("POC"))
  const isSalivaLab = regionID.startsWith("ORF")
  const isUrinePOCT = regionID.startsWith("CON")
  const isSalivaPOCT = regionID.startsWith("OCO")
  return {
    isUrineLab: isUrineLab,
    isSalivaLab: isSalivaLab,
    isUrinePOCT: isUrinePOCT,
    isSalivaPOCT: isSalivaPOCT
  }
}

export const showLabForSelectedCollectionType = (lab: LabAccount, collectionType: string): boolean => {
  return (
    (getLabType(lab).isSalivaLab && collectionType === "Saliva Lab Test") ||
    (getLabType(lab).isUrineLab && collectionType === "Urine Lab Test") ||
    (getLabType(lab).isSalivaPOCT && collectionType === "Saliva POCT") ||
    (getLabType(lab).isUrinePOCT && collectionType === "Urine POCT")
  )
}

export interface DataLoader<T> {
  data?: T[]
  loading?: boolean
  selected?: T
}

interface GovCollectionState {
  devices: DataLoader<GovDevice>
  labAccounts: DataLoader<LabAccount>
  panels: DataLoader<GovPanel>
  sampleTypes: DataLoader<SampleType>
  isPOCTDevice?: boolean
  defaultSampleTypeID?: number
  defaultLabAccount?: string
  defaultPanel?: string
  defaultDevice?: string
}

export default class GovCollection extends React.PureComponent<GovCollectionProps, GovCollectionState> {
  state: GovCollectionState = {
    devices: {},
    labAccounts: {},
    panels: {},
    sampleTypes: {},
    isPOCTDevice: false,
    defaultSampleTypeID: this.props.sampleTypeID,
    defaultLabAccount: this.props.labAccount,
    defaultPanel: this.props.panel,
    defaultDevice: this.props.device
  }

  labAccountRef = React.createRef<Typeahead<LabAccount>>()

  subscription = new Subscription()
  labAccountSubscription?: Subscription = undefined
  panelSubscription?: Subscription = undefined
  deviceSubscription?: Subscription = undefined

  componentDidMount() {
    this.loadSampleTypes()
  }

  componentDidUpdate(prevProps: GovCollectionProps) {
    const editModeChanged = prevProps.editing !== this.props.editing
    const formChanged = prevProps.sampleTypeID !== this.props.sampleTypeID || prevProps.labAccount !== this.props.labAccount || prevProps.panel !== this.props.panel || prevProps.device !== this.props.device
    if (editModeChanged && formChanged) {
      this.setState({
        defaultSampleTypeID: this.props.sampleTypeID,
        defaultLabAccount: this.props.labAccount,
        defaultPanel: this.props.panel,
        defaultDevice: this.props.device
      }, this.loadSampleTypes)
    }
  }

  componentWillUnmount() {
    this.labAccountSubscription && this.labAccountSubscription.unsubscribe()
    this.panelSubscription && this.panelSubscription.unsubscribe()
    this.deviceSubscription && this.deviceSubscription.unsubscribe()
    this.subscription.unsubscribe()
  }

  loadSampleTypes = () => {
    const labAccountNode = this.labAccountRef?.current
    if (labAccountNode) {
      labAccountNode.getInput().maxLength = LabAccountMaxLength
    }

    this.setState({ sampleTypes: { loading: true } })

    this.subscription.add(
      GovGetCollectionTypes().subscribe(
        (data: SampleType[]) => {
          const selectedSampleType = data.find(type => type.SampleTypeID === this.state.defaultSampleTypeID)
          const isPOCTDevice = selectedSampleType?.Description.includes("POCT")

          this.setState({ isPOCTDevice: isPOCTDevice, sampleTypes: { data: data, loading: false, selected: selectedSampleType }, defaultSampleTypeID: undefined })
          this.props.onTypeChange(selectedSampleType, isPOCTDevice)
          this.loadLabAccounts()
        },
        (error) => {
          this.setState({ sampleTypes: { loading: false } })
          toast.error(error?.errorData?.message || error.message)
        }
      )
    )
  }

  loadLabAccounts = () => {
    this.setState({
      labAccounts: { data: [], selected: undefined, loading: true },
      panels: { data: [], selected: undefined, loading: false },
      devices: { data: [], selected: undefined, loading: false }
    })
    this.labAccountSubscription && this.labAccountSubscription.unsubscribe()
    this.labAccountSubscription = LabAccountsGetObservable({
      SearchFor: "",
      SearchBy: "Account Name",
      CompanyID: this.props.companyID,
      RegionID: 0,
      LocationID: 0,
      DOT: false,
      IsMarketPlace: true
    }).subscribe(
      (data: LabAccount[]) => {
        const selectedLabAccount = data.find(item => item.LabAccount === this.state.defaultLabAccount)
        this.setState({
          labAccounts: { data: data, loading: false, selected: selectedLabAccount }
        })
        this.loadPanels(0, selectedLabAccount)
        this.loadDevices(selectedLabAccount)
        this.props.onLabChange(selectedLabAccount)
        if (!selectedLabAccount) {
          this.setState({ defaultPanel: undefined, defaultDevice: undefined })
        }
        this.filterSampleTypes()
      },
      () => this.setState({ labAccounts: { loading: false } })
    )
  }

  loadPanels = (device: number, selectedLabAccount?: LabAccount) => {
    if (!selectedLabAccount?.LabAccount) {
      this.setState({ panels: { data: [], selected: undefined, loading: false } })
      return
    }
    this.setState({ panels: { data: [], selected: undefined, loading: true } })

    if (this.state.isPOCTDevice) {
      if (!!this.state.devices.selected) {
        const selectedPanel = { ID: this.state.devices.selected?.PanelCode, Name: this.state.devices.selected?.PanelCode } as GovPanel
        this.setState({ panels: { data: [], selected: selectedPanel, loading: false } })
        this.props.onPanelChange(selectedPanel)
      }

      return
    }
    this.panelSubscription && this.panelSubscription.unsubscribe()
    this.panelSubscription = GovGetPanels(selectedLabAccount?.LabAccount, this.state.devices.selected?.DeviceID).subscribe(
      (data: SampleType[]) => {
        const selectedSampleType = data.find(type => type.SampleTypeID === this.state.sampleTypes.selected?.SampleTypeID)
        let selectedPanel = selectedSampleType?.Panels.find(p => p.ID === this.state.defaultPanel)
        this.setState({ panels: { data: selectedSampleType?.Panels, selected: selectedPanel, loading: false }, defaultPanel: undefined })
        this.props.onPanelChange(selectedPanel)
      },
      (error) => {
        this.setState({ panels: { loading: false } })
        toast.error(error?.errorData?.message || error.message)
      }
    )
  }

  loadDevices = (selectedLabAccount?: LabAccount) => {
    if (!selectedLabAccount?.LabAccount) {
      this.setState({ devices: { data: [], selected: undefined, loading: false }})
      return
    }
    this.setState({ devices: {data: [], selected: undefined, loading: true } })
    this.deviceSubscription && this.deviceSubscription.unsubscribe()
    this.deviceSubscription = GovDeviceGetObservable(selectedLabAccount?.LabAccount).subscribe(
      (data: GovDevice[]) => {
        const selectedDevice = data.find(item => item.Code === this.state.defaultDevice)
        const isPOCTDevice = this.state.isPOCTDevice
        const selectedSampleType = this.state.sampleTypes?.selected

        let filtered = data
        if (selectedSampleType && isPOCTDevice) {
          const sampleTypeCode: DeviceSampleType = DeviceSampleType[selectedSampleType.LabCode as keyof typeof DeviceSampleType]
          filtered = data?.filter(device => device.SampleType === sampleTypeCode)
        }

        this.setState({ devices: { data: filtered, selected: selectedDevice, loading: false }, defaultDevice: undefined })
        this.props.onDeviceChange(selectedDevice)
        if (isPOCTDevice) {
          const selectedPanel = { ID: selectedDevice?.PanelCode, Name: selectedDevice?.PanelCode } as GovPanel
          this.setState({ panels: { ...this.state.panels, selected: selectedPanel } })
          this.props.onPanelChange(selectedPanel)
        }
      },
      (error) => {
        this.setState({ devices: { loading: false } })
        toast.error(FormatError(error))
      }
    )
  }

  handleSampleTypeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const sampleTypeId = event.target.value
    const selectedSampleType = this.state.sampleTypes?.data?.find(type => type.SampleTypeID == Number(sampleTypeId))
    const isPOCTDevice = selectedSampleType?.Description.includes("POCT")
    this.setState({
      sampleTypes: { ...this.state.sampleTypes, selected: selectedSampleType },
      labAccounts: { ...this.state.labAccounts, selected: undefined },
      panels: { data: [], selected: undefined, loading: false },
      devices: { data: [], selected: undefined, loading: false },
      isPOCTDevice: isPOCTDevice,
      defaultLabAccount: undefined,
      defaultPanel: undefined,
      defaultDevice: undefined
    })
    this.props.onTypeChange(selectedSampleType, !!isPOCTDevice)
  }

  handleLabChange = (items: LabAccount[]) => {
    const selectedLab = items[0]
    this.setState({
      labAccounts: { ...this.state.labAccounts, selected: selectedLab },
      devices: { data:[], selected: undefined, loading: false },
      panels: { data:[], selected: undefined, loading: false }
    }, () => {
      this.props.onLabChange(selectedLab)
      this.loadPanels(0, selectedLab)
      this.loadDevices(selectedLab)
    })
  }

  handlePanelChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const panelId = event.target.value
    const panel = this.state.panels.data?.find(p => p.ID == panelId)
    this.setState({ panels: { ...this.state.panels, selected: panel } })
    this.props.onPanelChange(panel)
  }

  handleDeviceChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const deviceCode = event.target.value
    const selectedDevice = this.state.devices.data?.find(d => d.Code === deviceCode)
    const selectedPanel = { ID: selectedDevice?.PanelCode, Name: selectedDevice?.PanelCode } as GovPanel
    this.setState({ devices: { ...this.state.devices, selected: selectedDevice }, panels: { ...this.state.panels, selected: selectedPanel } })
    this.props.onDeviceChange(selectedDevice)
    this.props.onPanelChange(selectedPanel)
  }

  filterSampleTypes = () => {
    const filtered = this.state.sampleTypes.data?.filter(item => this.state.labAccounts.data?.some(lab => showLabForSelectedCollectionType(lab, item.Description)))
    this.setState(prevState => ({ sampleTypes: { ...prevState.sampleTypes, data: filtered } }))
  }

  populateSelectedOptions = <T extends object, K extends keyof T, V extends keyof T, D extends keyof T>(value: V, description: D, data?: T[], uniqKey?: K) => {
    return data?.map((o: T) => {
      const optionValue: string = o[value] as unknown as string
      const key: string = uniqKey ? o[uniqKey] as unknown as string : optionValue
      return <option value={optionValue} key={key}>{o[description]}</option>
    })
  }

  render() {
    const { showHeading, editing, labelClassName } = this.props
    const sampleTypeDisabled = this.state.sampleTypes.loading
    const labAccountDisabled = sampleTypeDisabled || this.state.labAccounts.loading || !this.state.sampleTypes.selected
    const panelsDisabled = labAccountDisabled || this.state.panels.loading || !this.state.labAccounts.selected
    const devicesDisabled = labAccountDisabled || this.state.devices.loading || !this.state.labAccounts.selected
    return (
      <Form.Group>
        {showHeading && <Form.Label className="font-weight-bold required">PLEASE SELECT SERVICE:</Form.Label>}
        <Form.Row className="form-group required align-items-center">
          <Form.Label column sm={5} htmlFor="panel" className={labelClassName}>Type of Collection</Form.Label>
          <Col sm={7}>
            {!editing && this.state.sampleTypes.loading && <Spinner animation="border" variant="secondary" size="sm" style={{ margin: "auto" }} />}
            {!editing && this.state.sampleTypes.selected?.Description}
            {editing && <Form.Control id="sampleType" as="select" disabled={sampleTypeDisabled} onChange={this.handleSampleTypeChange} value={this.state.sampleTypes.selected?.SampleTypeID}>
              <option value="">{this.state.sampleTypes.loading ? "Loading..." : "Select"}</option>
              {this.populateSelectedOptions("SampleTypeID", "Description", this.state.sampleTypes.data)}
            </Form.Control>}
          </Col>
        </Form.Row>
        <Form.Row className="form-group required align-items-center">
          <Form.Label column sm={5} htmlFor="labAccount" className={labelClassName}>{showHeading ? "" : "Default "}Laboratory / Account Number</Form.Label>
          <Col sm={7}>
            {!editing && this.state.labAccounts.loading && <Spinner animation="border" variant="secondary" size="sm" style={{ margin: "auto" }} />}
            {!editing && this.state.labAccounts.selected?.Description}
            {editing && <Typeahead id="labAccount"
              ref={this.labAccountRef}
              labelKey="Description"
              emptyLabel="No Laboratory Accounts Found"
              disabled={labAccountDisabled}
              filterBy={(option, props) => showLabForSelectedCollectionType(option, this.state.sampleTypes.selected?.Description || "")}
              onChange={this.handleLabChange}
              options={this.state.labAccounts.data || []}
              selected={this.state.labAccounts.selected ? [this.state.labAccounts.selected] : []}
              placeholder="Enter the Lab Account">
              <Button variant="toggle"
                disabled={labAccountDisabled}
                onClick={() => { this.labAccountRef.current?.toggleMenu() }}>
                <FaAngleDown />
              </Button>
            </Typeahead>}
          </Col>
        </Form.Row>
        {!this.state.isPOCTDevice && <Form.Row className="form-group required align-items-center">
          <Form.Label column sm={5} htmlFor="panel" className={labelClassName}>Test to Perform</Form.Label>
          <Col sm={7}>
            {!editing && this.state.panels.loading && <Spinner animation="border" variant="secondary" size="sm" style={{ margin: "auto" }} />}
            {!editing && this.state.panels.selected?.Name}
            {editing && <Form.Control id="panel" as="select"
              disabled={panelsDisabled}
              onChange={this.handlePanelChange}
              value={this.state.panels.selected?.ID}>
              <option value="">{this.state.panels.loading ? "Loading..." : "Select"}</option>
              {this.populateSelectedOptions("ID", "Name", this.state.panels.data)}
            </Form.Control>}
          </Col>
        </Form.Row>}
        {this.state.isPOCTDevice &&
          <Form.Row className="form-group required align-items-center">
            <Form.Label column sm={5} htmlFor="device" className={labelClassName}>POCT Device</Form.Label>
            <Col sm={7}>
              {!editing && this.state.devices.loading && <Spinner animation="border" variant="secondary" size="sm" style={{ margin: "auto" }} />}
              {!editing && this.state.devices.selected?.Description}
              {editing && <Form.Control id="device" as="select"
                disabled={devicesDisabled}
                onChange={this.handleDeviceChange}
                value={this.state.devices.selected?.Code}>
                <option value="">Select</option>
                {this.populateSelectedOptions("Code", "Description", this.state.devices.data)}
              </Form.Control>}
            </Col>
          </Form.Row>
        }
      </Form.Group>
    )
  }

  static defaultProps = {
    editing: false,
    pendingRandom: false
  }
}
